/**
 * Encryption / decryption utilities matching the two schemes Kit19 uses.
 *
 * 1. EncryptDecrypt.cs (Kit19_V2_Classes/Common) — used for `?q=` payloads:
 *    - Key derivation: PBKDF2 (Rfc2898DeriveBytes) SHA-256, 1000 iterations, 256-bit key
 *    - AES-256-CBC with PKCS7 padding
 *    - Byte layout: [32 bytes Salt] + [16 bytes IV] + [Ciphertext], Base64
 *
 * 2. jsFlowCommon (assets/custom/Settings/js/jsFlowCommon.js) — used for the
 *    `entityID` query parameter on every entity link: CryptoJS.AES passphrase
 *    mode (OpenSSL "Salted__" format), secret 'kit19Flow'. See flowEncrypt /
 *    flowDecrypt at the bottom of this file.
 *
 * Both are implemented with crypto-js — real AES, interoperable with the .NET
 * and legacy-JS sides.
 */

import CryptoJS from 'crypto-js'

const DEFAULT_PASSPHRASE = 'Kit19_SecretKey'
const DERIVATION_ITERATIONS = 1000
const KEY_SIZE_BYTES = 32 // 256-bit key
const SALT_SIZE_BYTES = 32 // Generate256BitsOfRandomEntropy
const IV_SIZE_BYTES = 16 // Generate128BitsOfRandomEntropy

function toUrlSafeBase64(base64: string): string {
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromUrlSafeBase64(urlSafe: string): string {
  let base64 = urlSafe.replace(/-/g, '+').replace(/_/g, '/')
  while (base64.length % 4 !== 0) {
    base64 += '='
  }
  return base64
}

/** Rfc2898DeriveBytes(passPhrase, salt, 1000, HashAlgorithmName.SHA256). */
function deriveKey(passPhrase: string, salt: CryptoJS.lib.WordArray): CryptoJS.lib.WordArray {
  return CryptoJS.PBKDF2(passPhrase, salt, {
    keySize: KEY_SIZE_BYTES / 4,
    iterations: DERIVATION_ITERATIONS,
    hasher: CryptoJS.algo.SHA256,
  })
}

/** Extracts a byte range of a WordArray as a new WordArray. */
function sliceWords(words: CryptoJS.lib.WordArray, startByte: number, lengthBytes: number): CryptoJS.lib.WordArray {
  const bytes: number[] = []
  for (let i = 0; i < lengthBytes; i++) {
    const byteIndex = startByte + i
    const word = words.words[byteIndex >>> 2]
    bytes.push((word >>> (24 - (byteIndex % 4) * 8)) & 0xff)
  }
  const out = CryptoJS.lib.WordArray.create()
  out.sigBytes = lengthBytes
  out.words = []
  for (let i = 0; i < lengthBytes; i += 4) {
    out.words.push(
      ((bytes[i] ?? 0) << 24) | ((bytes[i + 1] ?? 0) << 16) | ((bytes[i + 2] ?? 0) << 8) | (bytes[i + 3] ?? 0),
    )
  }
  return out
}

/**
 * Encrypts plain text matching Kit19's EncryptDecrypt.Encrypt:
 * random 32-byte salt + 16-byte IV, PBKDF2-SHA256 key, AES-256-CBC/PKCS7,
 * output = Base64([salt][iv][ciphertext]) made URL-safe.
 */
export function encryptText(plainText: string, passPhrase: string = DEFAULT_PASSPHRASE): string {
  if (!plainText) return ''
  try {
    const salt = CryptoJS.lib.WordArray.random(SALT_SIZE_BYTES)
    const iv = CryptoJS.lib.WordArray.random(IV_SIZE_BYTES)
    const key = deriveKey(passPhrase, salt)

    const encrypted = CryptoJS.AES.encrypt(plainText, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    })

    const combined = salt.clone().concat(iv).concat(encrypted.ciphertext)
    return toUrlSafeBase64(CryptoJS.enc.Base64.stringify(combined))
  } catch (err) {
    console.error('Encrypt failed:', err)
    return encodeURIComponent(plainText)
  }
}

/**
 * Decrypts cipher text matching Kit19's EncryptDecrypt.Decrypt:
 * [32 bytes salt][16 bytes IV][ciphertext].
 */
export function decryptText(cipherText: string, passPhrase: string = DEFAULT_PASSPHRASE): string {
  if (!cipherText) return ''
  try {
    const bytes = CryptoJS.enc.Base64.parse(fromUrlSafeBase64(cipherText))

    if (bytes.sigBytes < SALT_SIZE_BYTES + IV_SIZE_BYTES + 1) {
      return safeDecode(cipherText)
    }

    const salt = sliceWords(bytes, 0, SALT_SIZE_BYTES)
    const iv = sliceWords(bytes, SALT_SIZE_BYTES, IV_SIZE_BYTES)
    const cipherBytes = sliceWords(
      bytes,
      SALT_SIZE_BYTES + IV_SIZE_BYTES,
      bytes.sigBytes - SALT_SIZE_BYTES - IV_SIZE_BYTES,
    )

    const key = deriveKey(passPhrase, salt)

    const decrypted = CryptoJS.AES.decrypt(
      CryptoJS.lib.CipherParams.create({ ciphertext: cipherBytes }),
      key,
      { iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 },
    )

    const text = decrypted.toString(CryptoJS.enc.Utf8)
    return text || safeDecode(cipherText)
  } catch {
    return safeDecode(cipherText)
  }
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/**
 * Encrypts key-value params into a query string (`q=...`).
 */
export function encryptQueryParams(
  params: Record<string, string | number | boolean>,
  passPhrase: string = DEFAULT_PASSPHRASE,
): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.set(key, String(value))
    }
  }
  const plainString = searchParams.toString()
  const encrypted = encryptText(plainString, passPhrase)
  return `q=${encrypted}`
}

/**
 * Decrypts a query string (or `q` parameter value) into a key-value map.
 * Plain (unencrypted) query strings pass through unchanged.
 */
export function decryptQueryParams(
  searchOrQ: string,
  passPhrase: string = DEFAULT_PASSPHRASE,
): Record<string, string> {
  if (!searchOrQ) return {}

  let qValue = searchOrQ
  if (searchOrQ.startsWith('?')) {
    searchOrQ = searchOrQ.substring(1)
  }

  const parsedParams = new URLSearchParams(searchOrQ)
  if (parsedParams.has('q')) {
    qValue = parsedParams.get('q') || ''
  }

  if (!qValue || !parsedParams.has('q')) {
    const result: Record<string, string> = {}
    parsedParams.forEach((val, key) => {
      result[key] = val
    })
    return result
  }

  const decryptedText = decryptText(qValue, passPhrase)
  if (!decryptedText) return {}

  if (decryptedText.trim().startsWith('{')) {
    try {
      return JSON.parse(decryptedText)
    } catch {
      // Fall through to querystring parsing.
    }
  }

  const result: Record<string, string> = {}
  const decryptedParams = new URLSearchParams(decryptedText)
  decryptedParams.forEach((val, key) => {
    result[key] = val
  })

  if (Object.keys(result).length === 0 && decryptedText.includes('=')) {
    decryptedText.split('&').forEach((pair) => {
      const [k, v] = pair.split('=')
      if (k) result[decodeURIComponent(k)] = decodeURIComponent(v || '')
    })
  }

  return result
}

/* -------------------------------------------------------------------------- */
/* jsFlowCommon — the scheme the entity links actually use                     */
/* -------------------------------------------------------------------------- */

/**
 * `jsFlowCommon.Secret` in assets/custom/Settings/js/jsFlowCommon.js.
 *
 * Every `EntityViewDetails.aspx?entityType=…&entityID=…` / `EntityEdit.aspx?…`
 * link in the legacy app builds `entityID` with `jsFlowCommon.Encrypt(id)`, and
 * both pages open with `jsFlowCommon.Decrypt($("#entityID").val())`. That is
 * CryptoJS's OpenSSL-compatible format ("Salted__" + 8-byte salt, key/IV derived
 * with MD5) — not the PBKDF2 layout EncryptDecrypt.cs uses above, so the two are
 * not interchangeable.
 */
const FLOW_SECRET = 'kit19Flow'

/** Port of `jsFlowCommon.Encrypt` — URI-encoded, exactly as the links carry it. */
export function flowEncrypt(value: string | number): string {
  if (value === '' || value === null || value === undefined) return ''
  return encodeURIComponent(CryptoJS.AES.encrypt(String(value), FLOW_SECRET).toString())
}

/**
 * Port of `jsFlowCommon.Decrypt`. Returns '' when the value was not produced by
 * `flowEncrypt` — the legacy pages treat that as access denied.
 */
export function flowDecrypt(hashed: string): string {
  if (!hashed) return ''
  try {
    // The value may still be percent-encoded when read straight off location.search.
    let value = hashed.includes('%') ? decodeURIComponent(hashed) : hashed
    // A '+' that went through URL query parsing comes back as a space; the
    // CryptoJS base64 alphabet never contains spaces, so restore it.
    value = value.replace(/ /g, '+')
    return CryptoJS.AES.decrypt(value, FLOW_SECRET).toString(CryptoJS.enc.Utf8)
  } catch {
    return ''
  }
}
